/**
 * F-148 §10.2 supplier combobox (existing or new). Accessible combobox pattern without a native
 * select: text input with `role="combobox"`, a listbox of store suppliers filtered server-side,
 * and a "Create supplier" option when the typed name matches no existing supplier.
 * Supplier names are merchant content and render raw in `<bdi>`.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Plus } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import {
  RECEIVING_LIMITS,
  normalizeKey,
  type ReceivingSupplier,
  type ReceivingSupplierInput,
} from '@dukanti/shared/admin';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { withRawValues } from '../lib/error-messages';

export interface SupplierComboboxProps {
  readonly value: ReceivingSupplierInput | null;
  /** Display name of the current value (existing supplier name or the new name). */
  readonly displayName: string;
  readonly onChange: (value: ReceivingSupplierInput | null, displayName: string) => void;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly suppliers: readonly ReceivingSupplier[];
  readonly isLoading?: boolean;
  readonly disabled?: boolean;
  readonly error?: string | null;
  readonly className?: string;
}

type Option = { kind: 'existing'; supplier: ReceivingSupplier } | { kind: 'new'; name: string };

export function SupplierCombobox({
  value,
  displayName,
  onChange,
  query,
  onQueryChange,
  suppliers,
  isLoading = false,
  disabled = false,
  error = null,
  className,
}: SupplierComboboxProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const inputId = `${id}-input`;
  const listId = `${id}-listbox`;
  const errorId = `${id}-error`;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const trimmed = query.trim();
  const typedKey = normalizeKey(trimmed);
  const exactExisting = useMemo(
    () => suppliers.find((s) => normalizeKey(s.name) === typedKey) ?? null,
    [suppliers, typedKey],
  );

  const options = useMemo<Option[]>(() => {
    const list: Option[] = suppliers.map((supplier) => ({ kind: 'existing', supplier }));
    if (trimmed.length > 0 && !exactExisting) {
      list.push({ kind: 'new', name: trimmed.slice(0, RECEIVING_LIMITS.maxSupplierNameLength) });
    }
    return list;
  }, [suppliers, trimmed, exactExisting]);

  useEffect(() => {
    setActiveIndex((index) => Math.min(index, Math.max(options.length - 1, 0)));
  }, [options.length]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const choose = (option: Option): void => {
    if (option.kind === 'existing') {
      onChange({ kind: 'existing', supplierId: option.supplier.id }, option.supplier.name);
      onQueryChange(option.supplier.name);
    } else {
      onChange({ kind: 'new', name: option.name }, option.name);
      onQueryChange(option.name);
    }
    setOpen(false);
  };

  const commitTyped = (): void => {
    if (trimmed.length === 0) {
      if (value !== null) onChange(null, '');
      return;
    }
    if (exactExisting) {
      if (value?.kind !== 'existing' || value.supplierId !== exactExisting.id) {
        onChange({ kind: 'existing', supplierId: exactExisting.id }, exactExisting.name);
      }
      return;
    }
    const name = trimmed.slice(0, RECEIVING_LIMITS.maxSupplierNameLength);
    if (value?.kind !== 'new' || value.name !== name) onChange({ kind: 'new', name }, name);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(options.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      if (open && options[activeIndex]) {
        event.preventDefault();
        choose(options[activeIndex]);
      } else if (trimmed.length > 0) {
        event.preventDefault();
        commitTyped();
      }
    } else if (event.key === 'Escape') {
      if (open) {
        event.preventDefault();
        setOpen(false);
      }
    }
  };

  const activeId = open && options[activeIndex] ? `${listId}-${activeIndex}` : undefined;
  const selectedExisting = value?.kind === 'existing' ? value.supplierId : null;

  return (
    <div ref={rootRef} className={cn('relative space-y-1 text-start', className)}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={inputId} className="text-sm">
          {t('admin.receiving.new.supplier')}
        </Label>
        {value?.kind === 'existing' ? (
          <Badge variant="success" data-testid="supplier-existing-badge">
            <Check aria-hidden="true" className="me-1 size-3" />
            {t('admin.receiving.new.supplierExistingMatch')}
          </Badge>
        ) : null}
      </div>
      <div className="relative">
        <Input
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          autoComplete="off"
          value={query}
          placeholder={t('admin.receiving.new.supplierPlaceholder')}
          disabled={disabled}
          maxLength={RECEIVING_LIMITS.maxSupplierNameLength}
          onChange={(event) => {
            onQueryChange(event.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Low-effort entry: a typed name becomes a new supplier when the field is left.
            setTimeout(() => {
              if (!rootRef.current?.contains(document.activeElement)) commitTyped();
            }, 0);
          }}
          onKeyDown={onKeyDown}
          className="min-h-11 pe-10 text-start"
          data-testid="supplier-combobox-input"
        />
        {isLoading ? (
          <Loader2
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 animate-spin motion-reduce:animate-none"
          />
        ) : null}
      </div>
      {value ? (
        <p className="text-muted-foreground text-xs" data-testid="supplier-selected">
          <bdi>{displayName}</bdi>
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-destructive text-xs" role="alert">
          {error}
        </p>
      ) : null}
      <ul
        id={listId}
        role="listbox"
        aria-label={t('admin.receiving.new.supplier')}
        hidden={!open || options.length === 0}
        className="border-border bg-card shadow-admin-elevated absolute inset-x-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-lg border p-1"
        data-testid="supplier-combobox-list"
      >
        {options.map((option, index) => {
          const optionId = `${listId}-${index}`;
          const active = index === activeIndex;
          const selected = option.kind === 'existing' && option.supplier.id === selectedExisting;
          return (
            <li
              key={option.kind === 'existing' ? option.supplier.id : `new:${option.name}`}
              id={optionId}
              role="option"
              aria-selected={selected}
              className={cn(
                'flex min-h-11 cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm',
                active ? 'bg-admin-surface-muted' : '',
                selected ? 'font-semibold' : '',
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
              data-testid={option.kind === 'existing' ? 'supplier-option' : 'supplier-option-new'}
            >
              {option.kind === 'existing' ? (
                <bdi className="truncate">{option.supplier.name}</bdi>
              ) : (
                <>
                  <Plus aria-hidden="true" className="size-4 shrink-0" />
                  <span className="truncate">
                    {t('admin.receiving.new.supplierNew', withRawValues({ name: option.name }))}
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
